import React, { useEffect, useState, useCallback } from "react";
import { Target, Save, Plus, Trash2, Info } from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, Input, Select } from "../../lib/ui";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";

export default function HRKpiTemplates() {
  const toast = useToast();
  const { user } = useAuth();
  const canManage = ["SUPER_ADMIN", "OWNER", "HR_MANAGER"].includes(user?.role);
  const [templates, setTemplates] = useState([]);
  const [sources, setSources] = useState([]);
  const [pos, setPos] = useState("");
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([api.get("/hr/kpi-templates"), api.get("/hr/kpi-templates/sources")]);
      setTemplates(t.data.templates);
      setSources(s.data.sources);
      setPos((p) => p || t.data.templates[0]?.position);
    } catch { toast.error("Gagal memuat template KPI"); } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const tpl = templates.find((t) => t.position === pos);
    if (tpl) setMetrics(tpl.metrics.map((m) => ({ ...m })));
  }, [pos, templates]);

  const totalWeight = metrics.reduce((s, m) => s + (Number(m.weight) || 0), 0);

  const updateMetric = (i, patch) => setMetrics(metrics.map((m, idx) => idx === i ? { ...m, ...patch } : m));
  const addMetric = () => setMetrics([...metrics, { name: "", type: "manual", target: 100, weight: 0, unit: "%" }]);
  const removeMetric = (i) => setMetrics(metrics.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        position: pos,
        metrics: metrics.map((m) => ({
          name: m.name, type: m.type, source: m.type === "auto" ? m.source : undefined,
          target: Number(m.target) || 0, weight: Number(m.weight) || 0, unit: m.unit || "%",
          lower_is_better: m.lower_is_better || false,
        })),
      };
      await api.put("/hr/kpi-templates", payload);
      toast.success("Template KPI tersimpan"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); } finally { setSaving(false); }
  };

  return (
    <div data-testid="hr-kpi-templates-page">
      <PageHeader title="Template KPI" subtitle="Definisikan indikator KPI per posisi (evaluasi HR/Manajer + indikator otomatis)" icon={Target}>
        <Select value={pos} onChange={(e) => setPos(e.target.value)} data-testid="hr-kpitpl-position" className="!w-44">
          {templates.map((t) => <option key={t.position} value={t.position}>{t.position}</option>)}
        </Select>
      </PageHeader>

      {loading ? <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div> : (
        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-xl bg-blue-50 px-4 py-2.5 text-xs text-blue-800" data-testid="hr-kpitpl-note">
            <Info size={14} className="mt-0.5 shrink-0" />
            KPI utama dinilai oleh HR/Manajer. Indikator <b>Otomatis</b> (Kehadiran, Keterlambatan, dll) bersifat pendukung. Total bobot disarankan 100%.
          </div>

          <Card data-testid="hr-kpitpl-editor">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-heading font-bold text-ink">Metrik — {pos}</h3>
              <Badge tone={totalWeight === 100 ? "success" : "warn"} data-testid="hr-kpitpl-total-weight">Total Bobot {totalWeight}%</Badge>
            </div>

            <div className="space-y-3">
              {metrics.map((m, i) => (
                <div key={i} className="rounded-xl border border-line p-3" data-testid={`hr-kpitpl-metric-${i}`}>
                  <div className="grid gap-3 sm:grid-cols-12">
                    <div className="sm:col-span-4">
                      <label className="cf-label">Nama Metrik</label>
                      {m.type === "auto" ? (
                        <Select value={m.source || ""} disabled={!canManage} data-testid={`hr-kpitpl-source-${i}`}
                          onChange={(e) => {
                            const src = sources.find((s) => s.key === e.target.value);
                            updateMetric(i, { source: e.target.value, name: src?.label || m.name, unit: src?.unit || "%", target: src?.default_target ?? m.target, lower_is_better: src?.lower_is_better || false });
                          }}>
                          <option value="">— pilih sumber —</option>
                          {sources.map((s) => <option key={s.key} value={s.key}>{s.label}{s.optional ? " (opsional)" : ""}</option>)}
                        </Select>
                      ) : (
                        <input className="cf-input" value={m.name} disabled={!canManage} placeholder="cth. Kepatuhan Jobdesk"
                          onChange={(e) => updateMetric(i, { name: e.target.value })} data-testid={`hr-kpitpl-name-${i}`} />
                      )}
                    </div>
                    <div className="sm:col-span-3">
                      <label className="cf-label">Jenis</label>
                      <Select value={m.type} disabled={!canManage} data-testid={`hr-kpitpl-type-${i}`}
                        onChange={(e) => updateMetric(i, { type: e.target.value, source: e.target.value === "auto" ? m.source : undefined })}>
                        <option value="manual">Manual (Evaluasi)</option>
                        <option value="auto">Otomatis (Pendukung)</option>
                      </Select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="cf-label">Target</label>
                      <input type="number" className="cf-input" value={m.target} disabled={!canManage}
                        onChange={(e) => updateMetric(i, { target: e.target.value })} data-testid={`hr-kpitpl-target-${i}`} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="cf-label">Bobot %</label>
                      <input type="number" className="cf-input" value={m.weight} disabled={!canManage}
                        onChange={(e) => updateMetric(i, { weight: e.target.value })} data-testid={`hr-kpitpl-weight-${i}`} />
                    </div>
                    <div className="flex items-end sm:col-span-1">
                      {canManage && <button onClick={() => removeMetric(i)} className="rounded-lg p-2 text-danger-text hover:bg-red-50" data-testid={`hr-kpitpl-remove-${i}`}><Trash2 size={16} /></button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {canManage && (
              <div className="mt-4 flex items-center gap-2">
                <Button variant="outline" onClick={addMetric} data-testid="hr-kpitpl-add"><Plus size={15} /> Tambah Metrik</Button>
                <Button onClick={save} loading={saving} data-testid="hr-kpitpl-save"><Save size={15} /> Simpan Template</Button>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
